import { createTheme, type Theme } from '@mui/material/styles';
import { blue, grey, amber, orange } from '@mui/material/colors';
import type { TypographyProps } from '@mui/material/Typography';

declare module '@mui/material/styles' {
  interface TypeBackground {
    topbar: string;
    elevation1: string;
    elevation2: string;
  }

  interface PaletteColor {
    subtle?: string;
  }

  interface Palette {
    custom: {
      purple: string;
      gray: string;
      textColor: string;
      backgroundColor: string;
      topbar: string;
    };
    background: {
      paper: string;
      default: string;
      topbar: string;
      elevation1: string;
      elevation2: string;
    };
    slot: {
      normal: {
        active: string;
        hover: string;
        selected: string;
        border: string;
      };
      break: {
        active: string;
        hover: string;
        selected: string;
        border: string;
      };
      gap: {
        active: string;
        hover: string;
        selected: string;
        border: string;
      };
    };
  }

  interface PaletteOptions {
    custom?: {
      purple?: string;
      gray?: string;
      textColor?: string;
      backgroundColor?: string;
      topbar?: string;
    };
    background?: {
      paper?: string;
      default?: string;
      topbar?: string;
      elevation1?: string;
      elevation2?: string;
    };
    slot?: {
      normal?: {
        active?: string;
        hover?: string;
        selected?: string;
        border?: string;
      };
      break?: {
        active?: string;
        hover?: string;
        selected?: string;
        border?: string;
      };
      gap?: {
        active?: string;
        hover?: string;
        selected?: string;
        border?: string;
      };
    };
  }

  interface TypographyVariants {
    slotDuration: React.CSSProperties;
    slotStartTime: React.CSSProperties;
    subtitle3: React.CSSProperties;
    alertDescription: React.CSSProperties;
    /** Preview components (player-like layout) */
    heading1: React.CSSProperties;
    heading2: React.CSSProperties;
    heading3: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    slotDuration?: React.CSSProperties;
    slotStartTime?: React.CSSProperties;
    subtitle3?: React.CSSProperties;
    alertDescription?: React.CSSProperties;
    heading1?: React.CSSProperties;
    heading2?: React.CSSProperties;
    heading3?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    slotDuration: true;
    slotStartTime: true;
    subtitle3: true;
    alertDescription: true;
    heading1: true;
    heading2: true;
    heading3: true;
  }
}

type ThemeMode = 'light' | 'dark' | 'lowcontrast';

const themeColors = {
  light: {
    primary: { main: amber[600], contrastText: '#FFFFFF', dark: amber[700], light: amber[500] },
    secondary: { main: blue[600], subtle: 'rgba(25, 118, 210, 0.08)' },
    background: {
      paper: '#FFFFFF',
      default: '#FFFFFF',
      topbar: '#f7f7f7',
      elevation1: '#F7F7F7',
      elevation2: '#EFEFEF',
    },
    text: {
      primary: '#000000',
      secondary: 'rgba(0, 0, 0, 0.6)',
      disabled: 'rgba(0, 0, 0, 0.38)',
      hint: 'rgba(0, 0, 0, 0.38)',
      icon: 'rgba(0, 0, 0, 0.38)',
    },
    slot: {
      normal: {
        active: 'rgba(0, 0, 0, 0.08)',
        hover: 'rgba(0, 0, 0, 0.12)',
        selected: 'rgba(0, 0, 0, 0.12)',
        border: 'rgba(0, 0, 0, 0.12)',
      },
      break: {
        active: 'rgba(0, 0, 0, 0.04)',
        hover: 'rgba(0, 0, 0, 0.08)',
        selected: 'rgba(0, 0, 0, 0.12)',
        border: 'rgba(0, 0, 0, 0.24)',
      },
      gap: {
        active: 'rgba(0, 0, 0, 0.02)',
        hover: 'rgba(0, 0, 0, 0.04)',
        selected: 'rgba(0, 0, 0, 0.08)',
        border: 'rgba(0, 0, 0, 0.12)',
      },
    },
    custom: {
      purple: '#9747FF',
      gray: grey[400],
      textColor: 'inherit',
      backgroundColor: 'transparent',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      subtle: 'rgba(76, 175, 80, 0.08)',
    },
  },
  dark: {
    primary: {
      main: amber[600],
      contrastText: '#000000',
      dark: amber[700],
      light: amber[500],
    },
    warning: {
      main: orange[800],
      light: orange[700],
      dark: orange[900],
      subtle: 'rgba(255, 183, 77, 0.12)',
    },
    secondary: {
      main: blue[400],
      subtle: 'rgba(66, 165, 245, 0.12)',
    },
    background: {
      paper: '#121212',
      default: '#121212',
      topbar: '#181818',
      elevation1: '#1C1C1C',
      elevation2: '#272727',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
      disabled: 'rgba(255, 255, 255, 0.5)',
      hint: 'rgba(255, 255, 255, 0.5)',
      icon: 'rgba(255, 255, 255, 0.5)',
    },
    slot: {
      normal: {
        active: 'rgba(255, 255, 255, 0.16)',
        hover: 'rgba(255, 255, 255, 0.20)',
        selected: 'rgba(255, 255, 255, 0.20)',
        border: 'rgba(255, 255, 255, 0.20)',
      },
      break: {
        active: 'rgba(255, 255, 255, 0.16)',
        hover: 'rgba(255, 255, 255, 0.20)',
        selected: 'rgba(255, 255, 255, 0.20)',
        border: 'rgba(255, 255, 255, 0.32)',
      },
      gap: {
        active: 'rgba(255, 255, 255, 0.02)',
        hover: 'rgba(255, 255, 255, 0.04)',
        selected: 'rgba(255, 255, 255, 0.08)',
        border: 'rgba(255, 255, 255, 0.20)',
      },
    },
    custom: {
      purple: '#9747FF',
      gray: grey[600],
      textColor: '#ffffff',
      backgroundColor: '#181818',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      subtle: 'rgba(76, 175, 80, 0.12)',
    },
  },
  lowcontrast: {
    primary: {
      main: '#ffffff',
      contrastText: '#000000',
      dark: '#D2BF93',
      light: '#ECE3D1',
    },
    secondary: {
      main: '#DFD1B2',
      contrastText: '#000000',
      dark: amber[500],
      light: amber[300],
      subtle: 'rgba(223, 209, 178, 0.24)',
    },
    background: {
      default: '#604f26',
      paper: '#604f26',
      topbar: '#B29146',
      elevation1: '#6B5630',
      elevation2: '#77612F',
    },
    text: {
      primary: '#ffffff',
      secondary: '#ffffff',
      disabled: '#ffffff',
      hint: '#ffffff',
      icon: '#ffffff',
    },
    slot: {
      normal: {
        active: 'rgba(255, 255, 255, 0.12)',
        hover: 'rgba(255, 255, 255, 0.16)',
        selected: 'rgba(255, 255, 255, 0.20)',
        border: 'rgba(255, 255, 255, 0.20)',
      },
      break: {
        active: 'rgba(255, 255, 255, 0.12)',
        hover: 'rgba(255, 255, 255, 0.16)',
        selected: 'rgba(255, 255, 255, 0.20)',
        border: 'rgba(255, 255, 255, 0.32)',
      },
      gap: {
        active: 'rgba(255, 255, 255, 0.04)',
        hover: 'rgba(255, 255, 255, 0.12)',
        selected: 'rgba(255, 255, 255, 0.16)',
        border: 'rgba(255, 255, 255, 0.20)',
      },
    },
    custom: {
      purple: '#c9a5ff',
      gray: grey[300],
      textColor: '#666666',
      backgroundColor: '#f5f5f5',
    },
    success: {
      main: '#4caf50',
      light: '#81c784',
      dark: '#388e3c',
      subtle: 'rgba(76, 175, 80, 0.1)',
    },
  },
};

const commonComponents = {
  MuiButtonBase: {
    styleOverrides: {
      root: {
        '&:focus': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: 'none',
        },
        '&.Mui-focusVisible': {
          outline: 'none !important',
          boxShadow: 'none !important',
        },
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        '&.MuiButton-sizeSmall': {
          height: '22px',
          paddingLeft: '8px',
          paddingRight: '8px',
        },
        '&:focus': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: 'none',
        },
        '&.Mui-focusVisible': {
          outline: 'none !important',
          boxShadow: 'none !important',
        },
      },
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {},
    },
  },
  MuiIconButton: {
    styleOverrides: {
      root: {
        '&:focus': {
          outline: 'none',
        },
        '&:focus-visible': {
          outline: 'none',
        },
        '&.Mui-focusVisible': {
          outline: 'none !important',
          boxShadow: 'none !important',
        },
      },
    },
  },
  MuiCardHeader: {
    defaultProps: {
      titleTypographyProps: { variant: 'h6' as TypographyProps['variant'] },
    },
    styleOverrides: {
      root: {},
    },
  },
  MuiCardContent: {
    styleOverrides: {
      root: {
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
  },
  MuiList: {
    styleOverrides: {
      root: {
        '&.MuiList-root': {
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
        },
        'padding': 0,
      },
    },
  },
  MuiListItem: {
    styleOverrides: {
      root: ({ theme, ownerState }: { theme: Theme; ownerState?: Record<string, unknown> }) => {
        const isSelected = (ownerState?.selected as boolean) ?? false;
        return {
          '& .MuiTypography-root': {
            color: 'text.primary',
          },
          'border': 1,
          'borderColor': isSelected ? 'primary.main' : 'divider',
          'borderRadius': 2,
          'boxShadow': theme.shadows[1],
          'backgroundColor': theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(0, 0, 0, 0.02)',
          'transition': 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          'marginBottom': '4px',
          '&:hover': {
            boxShadow: theme.shadows[2],
          },
        };
      },
    },
  },
  MuiListItemText: {
    styleOverrides: {
      root: {
        color: 'text.primary',
      },
      primary: {
        color: 'text.primary',
      },
    },
  },
  MuiAccordion: {
    styleOverrides: {
      root: ({ theme }: { theme: Theme }) => ({
        '&:before': {
          display: 'none',
        },
        'backgroundColor': theme.palette.background.elevation2,
        'boxShadow': 'none',
        'borderRadius': 0,
        '&:not(:last-child)': {
          marginBottom: 8,
        },
      }),
    },
  },
  MuiAccordionSummary: {
    styleOverrides: {
      root: {
        '&:focus': {
          outline: 'none',
          backgroundColor: 'transparent',
        },
        '&.Mui-focused': {
          backgroundColor: 'transparent',
        },
        'padding': '0px',
      },
      content: {
        margin: 0,
      },
    },
  },
  MuiAccordionDetails: {
    styleOverrides: {
      root: {
        padding: '16px',
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        'minWidth': '24px',
        'width': '24px',
        'height': '24px',
        '& svg': {
          width: '24px',
          height: '24px',
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        marginBottom: '0px',
        paddingBottom: '0px',
      },
      indicator: {
        bottom: '0px',
      },
    },
  },
  MuiSelect: {
    styleOverrides: {
      icon: ({ theme }: { theme: Theme }) => ({
        'color': (theme.vars ?? theme).palette.text.primary,
        '&.MuiSelect-disabled': {
          color: (theme.vars ?? theme).palette.action.disabled,
        },
      }),
    },
  },
};

const commonTypography = {
  h6: {
    fontSize: '1.00rem',
    fontWeight: 500,
    lineHeight: 1.4,
    letterSpacing: '0.0075em',
  },
  body1: {
    fontFamily: 'Roboto',
    fontSize: '1rem',
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: '0.0075em',
  },
  subtitle1: {
    fontFamily: 'Roboto',
    fontSize: '0.875rem',
    fontWeight: 400,
    lineHeight: 1.2,
    letterSpacing: '0.033em',
  },
  slotStartTime: {
    fontFamily: 'Roboto',
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.0,
    letterSpacing: '0.033em',
    color: 'text.secondary',
  },
  heading1: {
    fontFamily: 'Inter, Roboto, sans-serif',
    fontSize: '3vw',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: 'normal',
    color: '#000',
  },
  heading2: {
    fontFamily: 'Inter, Roboto, sans-serif',
    fontSize: '1.7vw',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: 'normal',
    color: '#000',
  },
  heading3: {
    fontFamily: 'Inter, Roboto, sans-serif',
    fontSize: '1.2vw',
    fontStyle: 'normal',
    fontWeight: 700,
    lineHeight: 'normal',
    color: '#000',
  },
  slotDuration: {
    fontFamily: 'Roboto',
    fontSize: '0.75rem',
    fontWeight: 400,
    lineHeight: 1.0,
    letterSpacing: '0.033em',
    color: 'text.secondary',
  },
  subtitle3: {
    fontFamily: 'Roboto',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 300,
    lineHeight: '110%',
    letterSpacing: '0.4px',
    fontFeatureSettings: '"liga" off, "clig" off',
  },
  alertDescription: {
    fontFamily: 'Roboto',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '143%',
    letterSpacing: '0.15px',
  },
  caption: {
    fontFamily: 'Roboto',
    fontSize: '12px',
    fontStyle: 'normal',
    fontWeight: 300,
    lineHeight: '120%',
    letterSpacing: '0.4px',
  },
};

export type { ThemeMode };
export const theme = (mode: ThemeMode) =>
  createTheme({
    palette: {
      mode: mode === 'lowcontrast' ? 'light' : mode,
      ...themeColors[mode],
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 768,
        lg: 1200,
        xl: 1536,
      },
    },
    components: commonComponents,
    typography: commonTypography,
  });

export default theme;
